import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  Sparkles, Users2, Trophy, Wifi, Rabbit, Loader2, Gamepad2,
  Bell, Bot, Award, ArrowRight, Check,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";

export default function LandingPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [guestLoading, setGuestLoading] = useState(false);
  useSEO({
    title: "Arkadaşlarla Okey — Fiziksel + Online",
    description:
      "Klasik & 101 Okey oyna, arkadaş grupları kur, fiziksel masalarının skorlarını Okeydaş tutsun. Ücretsiz, mobil-öncelikli, Türkçe PWA.",
  });

  const onGuest = async () => {
    setGuestLoading(true);
    try {
      await api.post("/auth/guest");
      if (typeof refresh === "function") await refresh();
      toast.success("Misafir olarak giriş yaptınız 👋");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg text-foreground">
      {/* Decorative background */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 85% 10%, rgba(222,187,108,0.14) 0%, transparent 50%), radial-gradient(circle at 15% 95%, rgba(8,71,30,0.14) 0%, transparent 45%)",
        }}
      />

      {/* Top nav */}
      <header className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo size={36} testId="landing-logo" />
          <span className="font-display font-extrabold text-lg tracking-tight">Okeydaş</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => nav("/giris")} className="rounded-full h-9 px-4 text-sm" data-testid="nav-login">
            Giriş Yap
          </Button>
          <Button onClick={() => nav("/kayit")} className="rounded-full h-9 px-4 text-sm hidden sm:inline-flex" data-testid="nav-register">
            Ücretsiz Hesap
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 pt-4 sm:pt-10 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-in fade-in slide-in-from-bottom-3 duration-500">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold mb-5">
              <Sparkles className="h-3.5 w-3.5" /> Arkadaş Odaklı Okey
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Masayı kur, arkadaşlarını çağır,
              <br />
              <span className="text-primary">skorları Okeydaş tutsun.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
              Klasik ve 101 Okey'i çevrimiçi arkadaşlarınla oyna; fiziksel masalarının puanlarını
              otomatik tut; sıralamalarda yerini al. Ücretsiz, mobil, tamamen Türkçe.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-lg">
              <Button
                data-testid="hero-guest"
                onClick={onGuest}
                disabled={guestLoading}
                className="flex-1 h-12 rounded-full text-base font-semibold active:scale-[0.98] transition-transform"
              >
                {guestLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><Rabbit className="h-4 w-4 mr-1.5" /> Misafir Olarak Başla</>
                )}
              </Button>
              <Button
                data-testid="hero-register"
                onClick={() => nav("/kayit")}
                variant="outline"
                className="flex-1 h-12 rounded-full text-base font-semibold active:scale-[0.98] transition-transform"
              >
                Ücretsiz Hesap Oluştur <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>

            <ul className="mt-6 grid grid-cols-2 gap-2 text-xs text-muted-foreground max-w-lg">
              {[
                "Bahis/kumar yok — %100 arkadaş oyunu",
                "Aynı ekrandan fiziksel + online",
                "Bot ekleyerek her an oyna",
                "Uygulama gibi çalışır (PWA)",
              ].map((t) => (
                <li key={t} className="flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Hero visual — layered brand tiles */}
          <div className="relative hidden md:block">
            <HeroTilesArt />
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-14 border-t border-border">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-widest font-semibold text-primary mb-2">Ne sunuyor?</p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl">İki dünya, tek uygulama</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Fiziksel masana kağıt-kalem gerek yok. Online masana lobi gerek yok. Okeydaş her ikisini de tek karttan yönetiyor.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard
            Icon={Wifi}
            title="Online Masa"
            desc="4 kişi, kod paylaş, WebSocket üzerinden gerçek zamanlı Klasik veya 101 Okey."
          />
          <FeatureCard
            Icon={Gamepad2}
            title="Fiziksel Skor"
            desc="Ceza, taş toplamı ve el hesabı otomatik. Yılların defter tutma yükü bitti."
          />
          <FeatureCard
            Icon={Users2}
            title="Arkadaşlar & Gruplar"
            desc="Perşembe Kulübü'nden Bayram Masası'na — grup sıralamaları ve davet kodları."
          />
          <FeatureCard
            Icon={Bot}
            title="Bot Oyuncular"
            desc="Kolay / Orta / Zor 3 zorluk seviyesi. Kimse yoksa masayı bot ile doldur."
          />
          <FeatureCard
            Icon={Trophy}
            title="Sıralama & Rozetler"
            desc="Haftalık, aylık, tüm zamanlar liderlik + kilit açılan 10 farklı rozet."
          />
          <FeatureCard
            Icon={Bell}
            title="Anlık Bildirim"
            desc="Sıra sana geldiğinde ve davet aldığında Web Push ile hemen haberdar ol."
          />
        </div>
      </section>

      {/* How it works */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-14 border-t border-border">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-widest font-semibold text-primary mb-2">Nasıl çalışır?</p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl">3 adımda masana geç</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StepCard n="1" title="Odayı Kur" desc="Klasik ya da 101 seç, ismini ver, kural varyantlarını aç." />
          <StepCard n="2" title="Arkadaşlarını Çağır" desc="Kodu paylaş veya arkadaş listenden davet et." />
          <StepCard n="3" title="Oyna & Kaydet" desc="Kazanan otomatik geçmişe eklenir; sıralamada yerini alır." />
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8 sm:p-12 text-center overflow-hidden relative">
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(circle at 20% 20%, rgba(222,187,108,0.5) 0%, transparent 50%)",
            }}
          />
          <div className="relative z-10">
            <Award className="h-8 w-8 mx-auto mb-3 text-accent" />
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl">Hazır mısın?</h2>
            <p className="opacity-90 mt-3 max-w-md mx-auto">
              Tek tıkla misafir olarak dene; beğenirsen hesabını istatistiklerini kaybetmeden kalıcı yap.
            </p>
            <Button
              onClick={onGuest}
              disabled={guestLoading}
              data-testid="cta-guest"
              variant="secondary"
              className="rounded-full h-12 px-8 text-base font-bold mt-6 active:scale-[0.98] transition-transform"
            >
              {guestLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rabbit className="h-4 w-4 mr-1.5" /> Hemen Başla</>}
            </Button>
          </div>
        </div>
      </section>

      <footer className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Logo size={20} />
          <span>© 2026 Okeydaş · Bahis, kumar veya para ödülü içermez.</span>
        </div>
        <div className="flex gap-4">
          <button onClick={() => nav("/giris")} className="hover:text-foreground">Giriş</button>
          <button onClick={() => nav("/kayit")} className="hover:text-foreground">Kayıt</button>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display font-bold text-base">{title}</p>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}

function StepCard({ n, title, desc }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 relative overflow-hidden">
      <span className="absolute right-3 top-1 font-display font-extrabold text-6xl leading-none text-primary/10 select-none">
        {n}
      </span>
      <p className="text-xs font-semibold text-primary">Adım {n}</p>
      <p className="font-display font-bold text-lg mt-1">{title}</p>
      <p className="text-sm text-muted-foreground mt-1">{desc}</p>
    </div>
  );
}

/** Pure-CSS decorative art: a floating stack of okey tiles. Avoids external images. */
function HeroTilesArt() {
  const tiles = [
    { c: "text-red-600 border-red-200 bg-red-50", n: 7, rot: -8, tx: 0, ty: 0 },
    { c: "text-emerald-700 border-emerald-200 bg-emerald-50", n: 5, rot: 6, tx: 55, ty: 20 },
    { c: "text-blue-700 border-blue-200 bg-blue-50", n: 12, rot: -4, tx: -60, ty: 45 },
    { c: "text-yellow-700 border-yellow-200 bg-yellow-50", n: 3, rot: 12, tx: 90, ty: 90 },
    { c: "text-red-600 border-red-200 bg-red-50", n: 9, rot: -14, tx: -30, ty: 110 },
  ];
  return (
    <div className="relative h-96 w-full flex items-center justify-center">
      <div
        aria-hidden
        className="absolute w-72 h-72 rounded-full bg-primary/20 blur-3xl"
      />
      <div className="relative">
        {tiles.map((t, i) => (
          <div
            key={i}
            className={`absolute w-24 h-32 rounded-2xl border-2 shadow-xl flex items-center justify-center ${t.c} bg-white/95 dark:bg-white/95`}
            style={{
              transform: `translate(${t.tx}px, ${t.ty}px) rotate(${t.rot}deg)`,
              animation: `float 6s ease-in-out ${i * 0.3}s infinite`,
            }}
          >
            <div className="text-center">
              <div className="font-display font-extrabold text-5xl leading-none">{t.n}</div>
              <div className="text-[10px] uppercase tracking-widest mt-2 opacity-70">Okey</div>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes float {
          0%, 100% { translate: 0 0; }
          50% { translate: 0 -8px; }
        }
      `}</style>
    </div>
  );
}
